import { useCallback, useEffect, useRef, useState } from 'react';
import { api, errorText, formatDate, formatQty } from '../api';
import { Badge, Card, Empty, Notice } from '../components/ui';
import type { Article, Location, StockRow } from '../types';

/**
 * Scanner-Modus.
 *
 * Eine Ansicht für das Gerät in der Hand: grosses Eingabefeld, grosse Zahlen,
 * keine Tabellenwüste. Handscanner tippen ihre Eingabe wie eine Tastatur - das
 * funktioniert hier ohne Zutun. Zusätzlich die Handykamera, wo der Browser
 * Barcodes selbst erkennen kann.
 */

interface DetectedBarcode {
  rawValue: string;
}

interface BarcodeDetectorLike {
  detect: (source: CanvasImageSource) => Promise<DetectedBarcode[]>;
}

type BarcodeDetectorCtor = new (options?: { formats?: string[] }) => BarcodeDetectorLike;

const detectorAvailable = (): boolean =>
  typeof (window as unknown as { BarcodeDetector?: unknown }).BarcodeDetector === 'function';

type Result =
  | { kind: 'article'; article: Article; stock: StockRow[] }
  | { kind: 'location'; location: Location; stock: StockRow[] }
  | { kind: 'none'; term: string };

export function ScanPage() {
  const [term, setTerm] = useState('');
  const [result, setResult] = useState<Result | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [cameraOn, setCameraOn] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const lookup = useCallback(async (value: string) => {
    const code = value.trim();
    if (!code) return;

    setBusy(true);
    setError('');
    try {
      // Zuerst als Artikel deuten - das ist der häufigere Fall am Gerät.
      const articles = await api<Article[]>('/api/articles/lookup', { query: { q: code } });
      const exact =
        articles.find((article) => article.ean === code || article.sku === code) ?? articles[0];

      if (exact) {
        const stock = await api<StockRow[]>(`/api/stock/availability/${exact.id}`, {
          query: { ownerId: exact.owner_id },
        });
        setResult({ kind: 'article', article: exact, stock });
        return;
      }

      // Sonst als Lagerplatz.
      try {
        const location = await api<Location>(
          `/api/warehouse/locations/by-code/${encodeURIComponent(code)}`,
        );
        const stock = await api<StockRow[]>('/api/stock', { query: { locationId: location.id } });
        setResult({ kind: 'location', location, stock });
        return;
      } catch {
        setResult({ kind: 'none', term: code });
      }
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
      setTerm('');
      inputRef.current?.focus();
    }
  }, []);

  const stopCamera = useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    setCameraOn(false);
  }, []);

  useEffect(() => stopCamera, [stopCamera]);

  async function startCamera() {
    setError('');
    if (!detectorAvailable()) {
      setError(
        'Dieser Browser erkennt Barcodes nicht selbst. Ein Handscanner funktioniert trotzdem - einfach in das Feld scannen.',
      );
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' },
      });
      streamRef.current = stream;
      setCameraOn(true);

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }

      const Detector = (window as unknown as { BarcodeDetector: BarcodeDetectorCtor })
        .BarcodeDetector;
      const detector = new Detector({
        formats: ['ean_13', 'ean_8', 'code_128', 'code_39', 'qr_code'],
      });

      const tick = async () => {
        if (!streamRef.current || !videoRef.current) return;
        try {
          const codes = await detector.detect(videoRef.current);
          if (codes.length > 0 && codes[0]!.rawValue) {
            stopCamera();
            await lookup(codes[0]!.rawValue);
            return;
          }
        } catch {
          // Einzelne Fehlversuche sind normal, solange nichts im Bild ist.
        }
        requestAnimationFrame(() => void tick());
      };
      void tick();
    } catch {
      setError('Kein Kamerazugriff. Bitte im Browser erlauben.');
      stopCamera();
    }
  }

  return (
    <>
      {error && <Notice kind="error">{error}</Notice>}

      <Card>
        <label className="field" style={{ marginBottom: 8 }}>
          <span>Artikel oder Lagerplatz scannen</span>
          <input
            ref={inputRef}
            autoFocus
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void lookup(term);
            }}
            placeholder="EAN, Artikelnummer oder Platzcode"
            style={{ fontSize: 22, padding: '14px 12px' }}
          />
        </label>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className="primary" onClick={() => void lookup(term)} disabled={busy}>
            {busy ? 'Suche …' : 'Suchen'}
          </button>
          {cameraOn ? (
            <button onClick={stopCamera}>Kamera aus</button>
          ) : (
            <button onClick={() => void startCamera()}>Kamera einschalten</button>
          )}
          {result && <button onClick={() => setResult(null)}>Zurücksetzen</button>}
        </div>

        {cameraOn && (
          <video
            ref={videoRef}
            playsInline
            muted
            style={{
              width: '100%',
              maxWidth: 420,
              marginTop: 14,
              borderRadius: 6,
              border: '1px solid var(--line)',
            }}
          />
        )}
      </Card>

      {result?.kind === 'none' && (
        <Card>
          <Empty>
            Nichts gefunden zu <span className="mono">{result.term}</span>.
          </Empty>
        </Card>
      )}

      {result?.kind === 'article' && (
        <Card>
          <div style={{ fontSize: 20, fontWeight: 600 }}>{result.article.name}</div>
          <div className="small muted mono" style={{ marginBottom: 10 }}>
            {result.article.sku}
            {result.article.ean ? ` · ${result.article.ean}` : ''} · {result.article.owner_name}
          </div>

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
            {result.article.requires_lot && <Badge kind="info">chargenpflichtig</Badge>}
            {result.article.requires_serial && <Badge kind="info">Seriennummer</Badge>}
            {result.article.hazmat && <Badge kind="bad">Gefahrgut</Badge>}
            {result.article.min_stock > 0 && result.article.stock_total < result.article.min_stock && (
              <Badge kind="warn">unter Mindestbestand</Badge>
            )}
          </div>

          <div style={{ fontSize: 34, fontWeight: 600, lineHeight: 1.1 }}>
            {formatQty(result.article.stock_total, result.article.unit)}
          </div>
          <div className="small muted" style={{ marginBottom: 14 }}>
            Gesamtbestand über alle Lagerplätze
          </div>

          {result.stock.length === 0 ? (
            <Empty>Kein Bestand vorhanden.</Empty>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Lagerplatz</th>
                  <th>Charge / MHD</th>
                  <th className="num">Menge</th>
                </tr>
              </thead>
              <tbody>
                {result.stock.map((row) => (
                  <tr key={`${row.location_id}-${row.lot_id ?? 'none'}`}>
                    <td className="mono" style={{ fontSize: 15 }}>
                      {row.location_code}
                    </td>
                    <td className="small">
                      {row.lot_code ?? '—'}
                      {row.best_before ? ` · ${formatDate(row.best_before)}` : ''}
                    </td>
                    <td className="num" style={{ fontSize: 15, fontWeight: 600 }}>
                      {formatQty(row.qty, row.unit)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      )}

      {result?.kind === 'location' && (
        <Card>
          <div style={{ fontSize: 24, fontWeight: 600 }} className="mono">
            {result.location.code}
          </div>
          <div className="small muted" style={{ marginBottom: 14 }}>
            {result.location.warehouse_code}
            {result.location.zone_code ? ` · Zone ${result.location.zone_code}` : ''} ·{' '}
            {result.location.type}
            {result.location.capacity_units ? ` · Kapazität ${result.location.capacity_units}` : ''}
          </div>

          {result.stock.length === 0 ? (
            <Empty>Platz ist frei.</Empty>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Artikel</th>
                  <th>Eigentümer</th>
                  <th>Charge</th>
                  <th className="num">Menge</th>
                </tr>
              </thead>
              <tbody>
                {result.stock.map((row) => (
                  <tr key={`${row.article_id}-${row.lot_id ?? 'none'}`}>
                    <td style={{ fontSize: 14 }}>
                      <span className="mono">{row.sku}</span> {row.article_name}
                    </td>
                    <td className="small">{row.owner_name}</td>
                    <td className="small">{row.lot_code ?? '—'}</td>
                    <td className="num" style={{ fontSize: 15, fontWeight: 600 }}>
                      {formatQty(row.qty, row.unit)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      )}
    </>
  );
}
